import { Component } from '@theme/component';

/**
 * Mini-cart "Recommended for you" carousel.
 *
 * The drawer server-renders a fallback collection so the strip is never empty;
 * this element upgrades it to real Shopify recommendations for the most recently
 * added product.
 *
 * It deliberately does NOT use `morphSection`, which `product-recommendations.js`
 * relies on: inside the drawer the only hydration key is `cart-drawer-inner`, so
 * morphing the section would replace the whole cart with a copy rendered in the
 * recommendations context. Instead this swaps its own subtree.
 */
class CartRecommendations extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Response bodies keyed by request URL, so the re-fetch after a cart update is
   * free when the seed product has not changed.
   * @type {Record<string, string>}
   */
  #cache = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** The URL whose markup is currently rendered, so we never swap in the same HTML twice. */
  #renderedUrl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * A cart update re-renders the drawer, which morphs the server-rendered
   * fallback back over our injected markup. Re-apply it (from cache when the
   * seed product is unchanged).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#loadRecommendations();
  }

  get #requestUrl() {
    const { url, productId, sectionId, intent } = this.dataset;
    if (!url || !productId || !sectionId) return null;

    return `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`;
  }

  async #loadRecommendations() {
    const url = this.#requestUrl;

    // No seed product (empty cart, or a line whose product was deleted) — the
    // server-rendered fallback is all we can show, and that is fine.
    if (!url || url === this.#renderedUrl) return;

    try {
      const html = await this.#fetch(url);
      if (!html) return;

      const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-recommendations');

      // An empty response means Shopify had no recommendations for this product;
      // keep the fallback rather than blanking the strip.
      if (!incoming?.children.length) return;

      this.#renderedUrl = url;
      this.replaceChildren(...incoming.children);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Non-fatal: the fallback collection stays on screen.
      console.error('Cart recommendations error:', error);
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<string | null>}
   */
  async #fetch(url) {
    const cached = this.#cache[url];
    if (cached != null) return cached;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return null;

      const text = await response.text();
      this.#cache[url] = text;
      return text;
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
